import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import styled, { css } from "styled-components";

type Props = {
    fields: {
        firstImageItem: string;
        upperText1: string;
        bottonlinkText1: string;
        bottonlinkText2: string;
        bottonlinkText3: string;
        bottomlink1: string;
        bottomlink2: string;
        bottomlink3: string;
        middleImageItem: string;
        middleSectionText: string;
        middleSectionLink: string;
        middleButtonText: string;
        lastImageItem: string;
        lastSectionText: string;
        lastSectionLink: string;
    };
};

const subscribeStyles = {
    css: css`
        .parentGrid {
            width: 100%;
            border: 1px solid lightgray;
            padding: 1rem;
            display: flex;
            margin-bottom: 2rem;
            justify-content: center;
        }
        .parentGrid > div {
            flex-direction: row;
            padding: 0.5rem;
        }
        .parentGrid > .item-2 {
            padding-right: 1.5rem;
            color: rgb(0, 63, 121);
        }
        .item-2 {
            flex-direction: column;
        }
        .item-2 > div {
            padding: 0.1rem;
            color: rgb(0, 63, 121);
        }
        .child-item > a {
            padding-right: 0.2rem;
            color: rgb(0, 63, 121);
        }
        .item-1 > input {
            height: 80%;
            color: rgb(0, 63, 121);
            height: 40px;
            width: 100%;
            box-sizing: border-box;
            font-family: inherit;
            font-size: 15px;
            outline: 0;
            appearance: none;
            color: inherit;
            background: #f8f9fa;
            border-style: solid;
            border-width: 0 0 1px 0;
            border-color: #cccccc;
            padding: 0 10px 1px 10px;
            border-radius: 0;
        }
        .item-1 > a {
            color: rgb(0, 63, 121);
        }
        .item-1 > button {
            cursor: pointer;
            font-family: inherit;
            height: 40px;
            line-height: 1;
            backface-visibility: hidden;
            transition: all 200ms ease-in-out;
            padding: 0 21px;
            background-color: rgb(0, 63, 121);
            border: 2px solid rgb(0, 63, 121);
            color: #ffffff;
        }
        span {
            font-family: "Open Sans", "Roboto", "Lato", sans-serif;
            line-height: 1.5;
            font-size: 15px;
            font-weight: bold;
            color: rgb(1, 110, 175);
        }
    `,
};

const SubscribeWidget: React.FC<Props> = ({
    fields: {
        firstImageItem,
        upperText1,
        bottonlinkText1,
        bottonlinkText2,
        bottonlinkText3,
        bottomlink1,
        bottomlink2,
        bottomlink3,
        middleImageItem,
        middleSectionText,
        middleSectionLink,
        middleButtonText,
        lastImageItem,
        lastSectionText,
        lastSectionLink,
    },
}: Props) => {
    return (
        <StyledWrapper {...subscribeStyles}>
            <div className="parentGrid">
                <div className="item-1">
                    <img src={firstImageItem} alt="" width="42px" height="42px" />
                </div>
                <div className="item-2">
                    <div className="child-item">
                        <Typography> {upperText1}</Typography>
                    </div>
                    <div className="child-item">
                        <a href={bottomlink1}>
                            <Typography> {bottonlinkText1}</Typography>
                        </a>{" "}
                        |{" "}
                        <a href={bottomlink2}>
                            <Typography> {bottonlinkText2}</Typography>
                        </a>{" "}
                        |
                        <a href={bottomlink3}>
                            <Typography> {bottonlinkText3}</Typography>
                        </a>
                    </div>
                </div>
                <div className="item-1">
                    <img src={middleImageItem} alt="" width="42px" height="42px" />
                </div>
                <div className="item-1" style={{ width: "15%" }}>
                    <a href={middleSectionLink}>
                        <Typography> {middleSectionText}</Typography>
                    </a>
                </div>
                <div className="item-1">
                    <input type="text" placeholder="Enter your email" />
                </div>
                <div className="item-1">
                    <button>{middleButtonText}</button>
                </div>
                <div className="item-1">
                    <img src={lastImageItem} alt="" width="42px" height="42px" />
                </div>
                <div className="item-1" style={{ width: "6%" }}>
                    <a href={lastSectionLink}>
                        <Typography> {lastSectionText}</Typography>
                    </a>
                </div>
            </div>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SubscribeWidget,
    definition: {
        fieldDefinitions: [
            {
                name: "firstImageItem",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                tooltip:"Image Size has to be 330 x 229",
                displayName: "First Image",
                defaultValue: "",
            },
            {
                name: "upperText1",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "First Upper Text",
                defaultValue: "",
            },
            {
                name: "bottonlinkText1",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "First Bottom Text for link",
                defaultValue: "",
            },
            {
                name: "bottomlink1",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "First Bottom Link",
                defaultValue: "",
            },
            {
                name: "bottonlinkText2",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Second Bottom Text for link",
                defaultValue: "",
            },
            {
                name: "bottomlink2",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Second Bottom Link",
                defaultValue: "",
            },
            {
                name: "bottonlinkText3",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Third Bottom Text for link",
                defaultValue: "",
            },
            {
                name: "bottomlink3",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Third Bottom Link",
                defaultValue: "",
            },
            {
                name: "middleImageItem",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                tooltip: "Image Size has to be 330 x 229",
                displayName: "Middle Image",
                defaultValue: "",
            },
            {
                name: "middleSectionLink",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Middle Section Link",
                defaultValue: "",
            },
            {
                name: "middleSectionText",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Middle Section Text for link",
                defaultValue: "",
            },
            {
                name: "middleButtonText",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Middle Section Text for Button",
                defaultValue: "",
            },
            {
                name: "lastImageItem",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                tooltip: "Image Size has to be 330 x 229",
                displayName: "Last Image",
                defaultValue: "",
            },
            {
                name: "lastSectionLink",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Last Section Link",
                defaultValue: "",
            },
            {
                name: "lastSectionText",
                fieldType: "General",
                editorTemplate: "TextField",
                displayName: "Last Section Text for link",
                defaultValue: "",
            },
        ],
        group: "Common",
        icon: "Subscribe",
    },
};

export default widgetModule;
